import type { Client } from '../../api/client.js';
import { ajouterPersonne, validerAjout, type SaisieAjout } from './ajout-personne.js';
import { demanderPdf, exportNotaire, versCsv } from './export-notaire.js';
import { STATUTS_DEVOLUTION } from './libelles.js';
import type { DonneesArbre } from './types.js';

export const NOTE_EXEMPLE = 'Données d’exemple';

export interface RetourAjout {
  fini: boolean;
  erreurs: Record<string, string>;
  message: string | null;
  /** Le serveur attend un mandat signé (ou le forçage de la dirigeante) : l'écran ouvre l'avertissement. */
  mandatRequis?: boolean;
}

/**
 * Soumission du formulaire d'ajout. Avec un client, la personne est créée par l'API ; sans client (mode exemple),
 * la saisie est contrôlée mais rien n'est enregistré, et l'écran le dit.
 */
export async function soumettreAjout(
  saisie: SaisieAjout,
  client: Pick<Client, 'appeler'> | undefined,
  dossierId: string,
  /** L'arbre affiché : sert à exiger le parent commun d'un demi-frère quand la personne a deux parents. */
  donnees?: DonneesArbre,
): Promise<RetourAjout> {
  if (!client) {
    const v = validerAjout(saisie);
    return { fini: false, erreurs: v.erreurs, message: v.valide ? `${NOTE_EXEMPLE} : rien n’est enregistré. Cabinet MAAT` : null };
  }
  const r = await ajouterPersonne(saisie, client, { dossierId, ...(donnees ? { donnees } : {}) });
  return r.ok
    ? { fini: true, erreurs: {}, message: r.avertissement ?? null }
    : { fini: false, erreurs: r.erreurs ?? {}, message: r.message ?? null, ...(r.mandatRequis ? { mandatRequis: true } : {}) };
}

const ECHEC_EXPORT = 'L’export n’a pas abouti. Réessayez dans un instant ; si le problème persiste, contactez le Cabinet MAAT.';

/** Export pour le notaire : PDF demandé au serveur, ou, en mode exemple, CSV produit sur place. Renvoie le message à afficher. */
export async function lancerExport(
  d: DonneesArbre,
  client: Pick<Client, 'appeler'> | undefined,
  dossierId: string,
  telecharger: (nom: string, contenu: string) => void,
): Promise<string> {
  try {
    if (client) {
      await demanderPdf(client, dossierId);
      return `L’export pour le notaire est en cours de préparation. Droits : ${STATUTS_DEVOLUTION[d.devolution.status].mention}.`;
    }
    telecharger('tableau-des-vivants.csv', versCsv(exportNotaire(d)));
    return `${NOTE_EXEMPLE} : tableau téléchargé au format CSV.`;
  } catch {
    return ECHEC_EXPORT;
  }
}

/** Couloir G, point 5 : condition affichée quand l'arbre attend le mandat. */
export const CONDITION_MANDAT = 'Mandat signé au dossier';

/** Forçage tracé du blocage « mandat signé nécessaire » par la dirigeante, avec son motif (route `demarrage.forcer`). */
export async function forcerMandat(client: Pick<Client, 'appeler'>, dossierId: string, motif: string): Promise<boolean> {
  try {
    await client.appeler('demarrage.forcer', {
      parametres: { dossierId },
      corps: { portee: 'mandat_recherche', conditions: [CONDITION_MANDAT], motif },
    });
    return true;
  } catch {
    return false;
  }
}

/** L'assistante prévient la dirigeante (route `demarrage.demanderDeblocage`) : une tâche lui est créée. */
export async function demanderDeblocageMandat(client: Pick<Client, 'appeler'>, dossierId: string): Promise<boolean> {
  try {
    await client.appeler('demarrage.demanderDeblocage', {
      parametres: { dossierId },
      corps: { portee: 'mandat_recherche', conditions: [CONDITION_MANDAT] },
    });
    return true;
  } catch {
    return false;
  }
}
